/* eslint-disable complexity */
import { createHash } from "crypto";

import { useEffect, useState } from "react";
import * as EmailValidator from "email-validator";
import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import { z } from "zod";

import { Layout } from "~/components/common";
import { trackTikTok } from "~/libv2/tiktokTrack";
import { identifyTikTok } from "~/libv2/tiktokIdentity";
import { DropdownButtonSelect } from "~/components/contact/drop-down-btn";
import { RenderGoogleMaps } from "~/hooks";
import {
  CurveBanner,
  GradientWrap,
  Header,
  Title,
  Wrapper,
  Text,
  TextBold,
  ButtonWithArrow,
} from "~/ui";
import { useGlobalState } from "~/context";

export interface IContactInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  questionType: string;
  message: string;
}

export default function Contact() {
  const router = useRouter();
  const [errorMessage, setErrorMessage] = useState("");
  const { openFullScreenModal, closeFullScreenModal } = useGlobalState();
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [result, setResult] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [emailBlurred, setEmailBlurred] = useState(false);

  const schema = z.object({
    firstName: z.string().min(1, "Please enter a first name"),
    lastName: z.string().min(1, "Please enter a last name"),
    email: z
      .string()
      .email({
        message: "Please enter a valid email address",
      })
      .min(1, "Please enter an email address"),
    phone: z
      .string()
      .regex(/^[0-9]{8,10}$/, "Please enter a valid phone number")
      .optional()
      .or(z.literal("")),
    questionType: z.string({
      required_error: "Please select a question type",
    }),
    message: z.string().min(1, "Please enter a message"),
  });

  useEffect(() => {
    if (
      validationResult?.message === "Domain does not exist." ||
      validationResult?.message === "Internal server error."
    ) {
      //alert(JSON.stringify(validationResult))
      setIsValidEmail(false);
    }
    //alert(validationResult?.message)
  }, [validationResult]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, touchedFields, dirtyFields },
    reset,
    setValue,
    getValues,
  } = useForm<IContactInput>({
    resolver: zodResolver(schema),
    mode: "all",
  });

  async function onSubmitForm(values: IContactInput) {
    openFullScreenModal();
    try {
      if (errorMessage) {
        setErrorMessage("");
      }

      const eventId = v4();

      // Meta Pixel - Contact event (browser-side) - Fire BEFORE API call
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'Contact', {
          content_name: 'Contact Form Submission',
        }, { eventID: eventId });
      }

      // TikTok Pixel - Contact event (browser-side) - identify first for match quality
      identifyTikTok({ email: values.email, phone: values.phone });
      trackTikTok('Contact', {
        content_name: 'Contact Form Submission',
      }, eventId);
      
      // Push to data layer for GTM - Fire BEFORE API call
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'contact_form_submit',
          form_type: 'contact',
          event_id: eventId
        });
      }

      const responseOptions = {
        method: "POST",
        body: JSON.stringify(values),
        headers: {
          "Content-Type": "application/json",
        },
      };
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_FRONTEND_URL}/api/contact`,
        responseOptions
      );

      fetch('/api/meta/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          eventSourceUrl: typeof window !== 'undefined' ? window.location.href : router.asPath,
          em: createHash('sha256').update(values.email ?? '-').digest('hex'),
          fn: createHash('sha256').update(values.firstName || '-').digest('hex'),
          ln: createHash('sha256').update(values.lastName || '-').digest('hex'),
          ph: createHash('sha256').update(values.phone || '-').digest('hex'),
        }),
      });

      // Server-side TikTok Events API — same event_id; sends RAW PII, hashed server-side
      fetch('/api/tiktok/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          eventSourceUrl: typeof window !== 'undefined' ? window.location.href : router.asPath,
          email: values.email,
          phone: values.phone,
        }),
      }).catch(() => {});

      if (response.ok) {
        closeFullScreenModal();
        // toast.success('Message sent!')
        //console.log('Message sent!');
        reset();
      } else {
        throw new Error(response.statusText);
      }
      closeFullScreenModal();
    } catch (error) {
      closeFullScreenModal();
      //console.log('contact error', error);
      setErrorMessage("Something went wrong");
      if (error instanceof Error) {
        // toast.error(error.message)
        // console.error(error.message);
      }
    }
    closeFullScreenModal();
  }

  const containsErrorsFirstName = Object.keys(errors).includes("firstName");
  const containsErrorslastName = Object.keys(errors).includes("lastName");
  const containsErrorsEmail = Object.keys(errors).includes("email");
  const containsErrorsPhone = Object.keys(errors).includes("phone");
  const containsErrorsQuestionType =
    Object.keys(errors).includes("questionType");
  const containsErrorsMessage = Object.keys(errors).includes("message");
  const firstNameValidation =
    !containsErrorsFirstName && dirtyFields.firstName
      ? "primary-input-isValid"
      : touchedFields.firstName && "primary-input-isInvalid";
  const lastNameValidation =
    !containsErrorslastName && dirtyFields.lastName
      ? "primary-input-isValid"
      : touchedFields.lastName && "primary-input-isInvalid";
  const emailValidation =
    !containsErrorsEmail && dirtyFields.email
      ? "primary-input-isValid"
      : touchedFields.email && "primary-input-isInvalid";
  const phoneValidation =
    !containsErrorsPhone && getValues().phone && getValues().phone.length > 0
      ? "primary-input-isValid"
      : touchedFields.phone && containsErrorsPhone && "primary-input-isInvalid";
  const questionTypeValidation =
    !containsErrorsQuestionType && dirtyFields.questionType
      ? "primary-input-isValid"
      : errors.questionType && "primary-input-isInvalid";
  const messageValidation =
    !containsErrorsMessage && dirtyFields.message
      ? "primary-input-isValid"
      : touchedFields.message && "primary-input-isInvalid";

  const phoneFields = { ...register("phone") };
  const {
    name: phoneName,
    ref: phoneRef,
    onBlur: phoneOnBlur,
    onChange: phoneOnChange,
  } = phoneFields;
  //console.log('questionTypeValidation', questionTypeValidation);

  function getRestPartAfterFirstDot(domain) {
    const parts = domain.split(".");
    return parts.slice(1).join(".");
  }
  const verifyTLD = async (email1) => {
    try {
      const domain1 = email1.split("@")[1];
      // const domain2 = domain1.split(".")[2];
      const domain2 = getRestPartAfterFirstDot(domain1);
      const response = await fetch(`/api/mandrill/verify-email?tld=${domain2}`);
      const data = await response.json();
      setResult(data.exists);
    } catch (error) {
      // console.error("Failed to verify TLD:", error);
    }
  };

  const handleValidateEmail = async (domain) => {
    try {
      const domain1 = domain.split("@")[1];
      const response = await fetch("/api/mandrill/verify-email-deep", {
        //const response = await fetch("/api/mandrill/verify-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ domain: domain1 }),
      });

      if (response.ok) {
        const data = await response.json();
        setValidationResult(data);
      } else {
        setValidationResult(null);
      }
    } catch (error) {
      // console.error("Error:", error);
      setValidationResult(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;

    setIsValidEmail(EmailValidator.validate(fieldValue));
    handleValidateEmail(fieldValue);
    setValue(fieldName, fieldValue);
    // setEmail(fieldValue);
    verifyTLD(fieldValue);
  };
  const handleInputBlur = () => {
    setEmailBlurred(true);
  };

  return (
    <>
      <NextSeo
        title="Contact | Tucker Tub"
        description="Contact Description"
        openGraph={{
          title: "Contact | Tucker Tub",
          description: "Contact Description",
          url: process.env.NEXT_PUBLIC_FRONTEND_URL,
          type: "website",
          locale: "en_AU",
          site_name: "Tucker Tub",
        }}
      />

      <Layout>
        <GradientWrap className="bg-colorFourteen">
          <CurveBanner>
            <div className="container mx-auto">
              <h1 className="font-tucker-tub text-4xl font-bold uppercase text-colorFourteen lg:text-header">
                Contact
              </h1>
            </div>
          </CurveBanner>
          <Wrapper>
            <div className="h-[200px]" />
            <div className="grid grid-cols-1 gap-20 py-20 text-colorFifteen lg:grid-cols-2 lg:gap-[146px]">
              <div className="space-y-5">
                <Title>Tucker Tub Pet Food</Title>
                <Text>All natural dog food, made farm fresh in Australia.</Text>
                <TextBold className="text-lg">
                  Head Office & Production
                </TextBold>
                <div>
                  <p>218 High Street</p>
                  <p>BROADFORD VIC 3340</p>
                  <p>AUSTRALIA</p>
                </div>
                <div>
                  <Link href="tel:(03) 5784 3541" className="underline">
                    <Text>(03) 5784 3541</Text>
                  </Link>
                </div>
                <div>
                  <Link
                    href="mailto:woof@tuckertub.com.au"
                    className="underline"
                  >
                    <Text>woof@tuckertub.com.au</Text>
                  </Link>
                </div>
                <div className="my-12 max-h-[400px]">
                  <RenderGoogleMaps />
                </div>
              </div>
              <div className="max-w-[440px] space-y-5 sm:w-full">
                <Title>Ask a Question</Title>
                <Text>We'll woof right back!</Text>
                <form id="contact-form" onSubmit={handleSubmit(onSubmitForm)}>
                  <div className="grid grid-cols-1 space-y-3">
                    <div className="grid grid-cols-2 space-x-3">
                      <div>
                        <input
                          type="text"
                          // style={{
                          //   boxShadow: 'none',
                          //   borderColor: 'transparent'
                          // }}
                          className={`primary-input input-border ${firstNameValidation}`}
                          placeholder="First name*"
                          autoComplete="given-name"
                          aria-label="First name"
                          {...register("firstName")}
                        />
                        <p className="p-0 text-red-500">
                          {errors.firstName?.message}
                        </p>
                      </div>
                      <div>
                        <input
                          type="text"
                          // style={{
                          //   boxShadow: 'none',
                          //   borderColor: 'transparent'
                          // }}
                          className={`primary-input input-border ${lastNameValidation}`}
                          placeholder="Last name*"
                          autoComplete="family-name"
                          aria-label="Last name"
                          {...register("lastName")}
                        />
                        <p className="p-0 text-red-500">
                          {errors.lastName?.message}
                        </p>
                      </div>
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        // style={{
                        //   boxShadow: 'none',
                        //   borderColor: 'transparent'
                        // }}
                        className={`primary-input input-border ${emailValidation}`}
                        placeholder="Email*"
                        autoComplete="email"
                        aria-label="Email address"
                        //{...register("email")}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      />
                      {/* <p className="p-0 text-red-500">
                        {errors.email?.message}
                      </p> */}
                      {(validationResult?.valid && isValidEmail) ||
                      (result && isValidEmail) ? (
                        <p className="p-0 text-red-500">
                          {errors.email?.message}
                        </p>
                      ) : (
                        emailBlurred === true && (
                          <p className="p-0 text-red-500">
                            Please enter a valid email address.
                          </p>
                        )
                      )}
                    </div>
                    <div>
                      <input
                        type="tel"
                        maxLength={10}
                        // style={{
                        //   boxShadow: 'none',
                        //   borderColor: 'transparent'
                        // }}
                        className={`primary-input input-border ${phoneValidation}`}
                        placeholder="Phone"
                        autoComplete="home tel"
                        aria-label="Phone"
                        name={phoneName}
                        ref={phoneRef}
                        onBlur={phoneOnBlur}
                        onChange={(event) => {
                          const numericInput = event.target.value.replace(
                            /\D/g,
                            ""
                          );
                          event.target.value = numericInput;
                          phoneOnChange(event);
                        }}
                      />
                      <p className="p-0 text-red-500">
                        {phoneValidation && errors.phone?.message}
                      </p>
                    </div>
                    <div>
                      <DropdownButtonSelect
                        control={control}
                        questionTypeValidation={questionTypeValidation}
                      />
                      <p className="p-0 text-red-500">
                        {errors.questionType?.message}
                      </p>
                    </div>
                    <div>
                      <textarea
                        // style={{
                        //   boxShadow: 'none',
                        //   borderColor: 'transparent'
                        // }}
                        className={`primary-input input-border ${messageValidation} h-[100px] resize-none rounded-lg p-2`}
                        placeholder="Message"
                        autoComplete="off"
                        aria-label="Message"
                        rows={5}
                        {...register("message")}
                      />
                      <p className="p-0 text-red-500">
                        {errors.message?.message}
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="contact-mailing-list"
                        className="inline-flex items-center"
                      >
                        <input
                          type="checkbox"
                          className="mr-2 h-4 w-4 rounded-full border-colorFourteen text-colorTen outline-none focus:ring-transparent"
                          id="contact-mailing-list"
                          name="checkbox"
                        />
                        Sign up for the Tucker Tub mailing list
                      </label>
                    </div>
                    <div>
                      <ButtonWithArrow
                        buttonType="submit"
                        buttonVariant="primary"
                        disabled={
                          isSubmitting ||
                          !result ||
                          result === null ||
                          !isValidEmail
                        }
                      >
                        {isSubmitting ? "Sending..." : "Send"}
                      </ButtonWithArrow>
                    </div>
                    {errorMessage && (
                      <div>
                        <p className="p-0 text-red-500">{errorMessage}</p>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </Wrapper>
        </GradientWrap>
      </Layout>
    </>
  );
}